import { VisibilityState } from '@tanstack/react-table';
import { CompanySearchResult } from '@/lib/actions/company-search';

export const numberFormatter = new Intl.NumberFormat('de-DE');
export const secondaryTextClass = 'text-sm text-muted-foreground dark:text-zinc-300';
export const MOBILE_PAGE_SIZE = 10;

// Where logged-in users' column choices are persisted (localStorage via
// SettingsProvider). Guests always see the default set below.
export const VISIBLE_COLUMNS_SETTING = 'search.visibleColumns';
// Swiss choices live under their own key so they never overwrite the German
// ones and vice versa. A sibling, deliberately not `search.visibleColumns.CH`:
// SettingsProvider treats dots as a path, so a nested key would sit inside
// the German visibility object and be wiped every time that is stored.
export const VISIBLE_COLUMNS_SETTING_CH = 'search.visibleColumnsCH';
// Austrian choices likewise (same sibling-key rule).
export const VISIBLE_COLUMNS_SETTING_AT = 'search.visibleColumnsAT';

// Columns a logged-in user can toggle, in dropdown order. `name` and the `#`
// index column are intentionally absent — they are always visible anchors.
// `labelKey` maps to an i18n key under `search.table`.
export const TOGGLEABLE_COLUMNS: { id: string; labelKey: string }[] = [
  { id: 'city', labelKey: 'city' },
  { id: 'bundesland', labelKey: 'state' },
  { id: 'rechtsform', labelKey: 'rechtsform' },
  { id: 'industrySector', labelKey: 'industry' },
  { id: 'naceCode', labelKey: 'naceCode' },
  { id: 'employees', labelKey: 'employees' },
  { id: 'totalRevenue', labelKey: 'revenue' },
  { id: 'profit', labelKey: 'profit' },
  { id: 'domain', labelKey: 'website' },
  { id: 'email', labelKey: 'email' },
  { id: 'registerType', labelKey: 'registerType' },
  { id: 'registerId', labelKey: 'registerId' },
  { id: 'registerCourt', labelKey: 'registerCourt' },
  { id: 'kreis', labelKey: 'kreis' },
  { id: 'postalCode', labelKey: 'postalCode' },
  { id: 'cpvCodes', labelKey: 'cpvCodes' },
];

// Default visibility: the eight columns shown today stay on; the additional
// mv_search columns are available but off until the user enables them.
export const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
  // Off by default — the inline badge on the name already surfaces a
  // non-active status, so the column is for scanning and export.
  legalStatus: false,
  city: true,
  bundesland: true,
  rechtsform: false,
  industrySector: true,
  naceCode: false,
  employees: true,
  totalRevenue: true,
  profit: true,
  domain: true,
  email: true,
  registerType: false,
  registerId: false,
  registerCourt: false,
  kreis: false,
  postalCode: false,
  cpvCodes: false,
};

// Swiss scope (country=CH). Measured on mv_search: Swiss rows have city,
// canton, legal form, UID, register office and legal status throughout and a
// postal code for a good share — but no financials, headcount, industry,
// website, email or Kreis. Defaulting to the German set would show a grid of
// dashes, so the columns that carry data are on and the rest stay toggleable.
export const SWISS_DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
  legalStatus: true,
  city: true,
  bundesland: true,
  rechtsform: true,
  registerId: true,
  registerCourt: true,
  postalCode: true,
  industrySector: false,
  naceCode: false,
  employees: false,
  totalRevenue: false,
  profit: false,
  domain: false,
  email: false,
  registerType: false,
  kreis: false,
  cpvCodes: false,
};

// Swiss wording for columns whose German label names a German concept: the
// `bundesland` field holds the canton, the register id is the CHE-UID, and
// the "court" is a cantonal commercial register office. Keyed by column id,
// valued by an i18n key under `search.table`.
const SWISS_LABEL_KEYS: Record<string, string> = {
  bundesland: 'canton',
  registerId: 'uid',
  registerCourt: 'registerOffice',
};

// Austrian scope: the register number is the Firmenbuchnummer, the court the
// Firmenbuchgericht.
const AUSTRIAN_LABEL_KEYS: Record<string, string> = {
  registerId: 'fn',
  registerCourt: 'firmenbuchCourt',
};

export function columnLabelKey(
  id: string,
  defaultKey: string,
  country?: string,
): string {
  return (country === 'CH' && SWISS_LABEL_KEYS[id]) ||
    (country === 'AT' && AUSTRIAN_LABEL_KEYS[id]) || defaultKey;
}

// Columns offered in the Excel export dialog, in output order. `labelKey` maps
// to an i18n key under `search.table` (euId is special-cased to "EU-ID").
export const EXPORTABLE_COLUMNS: {
  id: string;
  labelKey: string;
  value: (r: CompanySearchResult) => string | number;
}[] = [
  { id: 'name', labelKey: 'name', value: (r) => r.name ?? '' },
  { id: 'city', labelKey: 'city', value: (r) => r.city ?? '' },
  {
    id: 'legalStatus',
    labelKey: 'legalStatus',
    value: (r) => r.legalStatus ?? 'active',
  },
  { id: 'bundesland', labelKey: 'state', value: (r) => r.bundesland ?? '' },
  { id: 'rechtsform', labelKey: 'rechtsform', value: (r) => r.rechtsform ?? '' },
  { id: 'kreis', labelKey: 'kreis', value: (r) => r.kreis ?? '' },
  { id: 'postalCode', labelKey: 'postalCode', value: (r) => r.postalCode ?? '' },
  {
    id: 'industrySector',
    labelKey: 'industry',
    value: (r) => r.industrySector ?? '',
  },
  { id: 'naceCode', labelKey: 'naceCode', value: (r) => r.naceCode ?? '' },
  { id: 'employees', labelKey: 'employees', value: (r) => r.employees ?? '' },
  // The financial figures carry their own fiscal year, because each one is
  // resolved to the most recent filing that actually reports it — a row's
  // revenue can be from 2021 while its profit is from 2024. The grid omits
  // the years to stay readable and because the company page shows the full
  // history, but an exported sheet leaves the product entirely, so the
  // columns are offered here. Off by default in DEFAULT_EXPORT_COLUMNS.
  {
    id: 'totalRevenue',
    labelKey: 'revenue',
    value: (r) => r.totalRevenue ?? '',
  },
  {
    id: 'totalRevenueYear',
    labelKey: 'revenueYear',
    value: (r) => r.totalRevenueYear ?? '',
  },
  { id: 'profit', labelKey: 'profit', value: (r) => r.profit ?? '' },
  {
    id: 'profitYear',
    labelKey: 'profitYear',
    value: (r) => r.profitYear ?? '',
  },
  {
    id: 'totalBalanceSum',
    labelKey: 'aktiva',
    value: (r) => r.totalBalanceSum ?? '',
  },
  {
    id: 'totalBalanceSumYear',
    labelKey: 'aktivaYear',
    value: (r) => r.totalBalanceSumYear ?? '',
  },
  {
    id: 'cpvCodes',
    labelKey: 'cpvCodes',
    value: (r) => r.cpvCodes?.join(', ') ?? '',
  },
  {
    id: 'registerType',
    labelKey: 'registerType',
    value: (r) => r.registerType ?? '',
  },
  {
    id: 'registerId',
    labelKey: 'registerId',
    value: (r) => r.registerId ?? '',
  },
  {
    id: 'registerCourt',
    labelKey: 'registerCourt',
    value: (r) => r.registerCourt ?? '',
  },
  { id: 'domain', labelKey: 'website', value: (r) => r.domain ?? '' },
  { id: 'email', labelKey: 'email', value: (r) => r.email ?? '' },
  { id: 'euId', labelKey: 'euId', value: (r) => r.euId ?? '' },
];

// Default-checked columns reproduce the previous fixed export output.
export const DEFAULT_EXPORT_COLUMNS = [
  'name',
  'city',
  'bundesland',
  'industrySector',
  'employees',
  'totalRevenue',
  'profit',
  'cpvCodes',
  'domain',
  'email',
  'euId',
];

// Swiss scope: the columns that carry data for Swiss companies (see
// SWISS_DEFAULT_COLUMN_VISIBILITY).
export const SWISS_DEFAULT_EXPORT_COLUMNS = [
  'name',
  'legalStatus',
  'city',
  'bundesland',
  'rechtsform',
  'postalCode',
  'registerId',
  'registerCourt',
  'euId',
];

export const EXPORT_COUNT_PRESETS = [100, 1000, 5000, 10000];
export const EXPORT_MAX = 10000;
